import type { FC } from 'react';
import React, { useEffect, useState } from 'react';

import { useTheme } from '@mui/material';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';

import cloudIng from '@assets/Images/onboarding/cloud-user-guide.png';
import financeImg from '@assets/Images/onboarding/finance-user-guide.png';
import googleSyncImg from '@assets/Images/onboarding/google-sync-user-guide.png';
import helpCenterImg from '@assets/Images/onboarding/help-center-user-guide.png';
import hubsImg from '@assets/Images/onboarding/hubs-user-guide.png';
import networkImg from '@assets/Images/onboarding/network-user-guide.png';
import scheduleImg from '@assets/Images/onboarding/schedule-user-guide.png';
import { updateEntityOnboardingTooltip } from '@pages/common/Onboarding/store/onboarding-slice';
import { updateOnboardingTooltips } from '@pages/common/Onboarding/store/onboarding-thunk';
import { EntityOnboardingTooltips } from '@pages/common/OnboardingTooltip/const/entity-onboarding-tooltips';
import { TooltipsStatusesEnum } from '@pages/common/OnboardingTooltip/const/tooltips-statuses.enum';
import { CustomTooltipTour } from '@pages/common/OnboardingTooltip/utils/custom-tooltip-tour';
import { AnalyticsEventEnum } from '@shared/enums/analyticsEvent.enum';
import useAnalyticsEventTracker from '@shared/hooks/analytics/useAnalyticsEventTracker';
import { useAppDispatch } from '@shared/hooks/redux';

import type { CallBackProps, Step } from 'react-joyride';

const defaultStepSettings = {
  disableBeacon: true,
  spotlightClicks: false,
  spotlightPadding: 0,
};
const mergedWithOptions = (steps: Step[]) => {
  return steps.map((step) => ({
    ...step,
    ...defaultStepSettings,
  }));
};

type OnboardingUserGuideProps = {
  isCompleteOnboarding: boolean;
};

export const OnboardingUserGuide: FC<OnboardingUserGuideProps> = ({ isCompleteOnboarding }) => {
  const theme = useTheme();
  const [run, setRun] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const dispatch = useAppDispatch();
  const gaEventTracker = useAnalyticsEventTracker();

  const ONBOARDING_TOOLTIP_STEPS = [
    {
      title: 'Onboarding Checklist:',
      content: 'Track your progress as you complete these steps.',
      placement: 'left' as const,
      target: '.onboarding-checklists-popover-container',
    },
    {
      title: 'Organize your schedule',
      content:
        'Let Hubmee helps  you  stay on top of your tasks and schedules by keeping  them all in one place.',
      img: scheduleImg,
      placement: 'right' as const,
      target: `.sidebarAgendaButton`,
    },
    {
      title: 'Store your document',
      content:
        'Store all your important documents in one place. Hubmee  organizes and secures them automatically.',
      img: cloudIng,
      placement: 'right' as const,
      target: `.sidebarCloudButton`,
    },
    {
      title: 'Manage your finances',
      content:
        'Take control of your finances with ease - track spending, save smarter, and reach your goals effortlessly.',
      img: financeImg,
      placement: 'right' as const,
      target: `.sidebarAccountingButton`,
    },
    {
      title: 'Manage every part of your routine in one place',
      content:
        'Add crucial info and documents to get timely updates on insurance, pet vaccinations, rent, and more.',
      img: hubsImg,
      placement: 'right' as const,
      target: `.sidebar-hubs`,
    },
    {
      title: 'Settings: Synchronization with Google',
      content:
        'Easily sync your Google account to integrate schedules, reminders, and tasks across all your devices.',
      img: googleSyncImg,
      placement: 'right' as const,
      target: '.sidebar-footer',
    },
    {
      title: 'Network',
      content: 'Find time to chat with loved ones. Ask about their news and share your own.',
      img: networkImg,
      placement: 'bottom' as const,
      target: '.target-header-network',
    },
    {
      title: 'Help Center',
      content:
        'Looking for help? Find answers, tips, and guides here. Get step-by-step instructions, helpful articles, and support for any issues you face.',
      img: helpCenterImg,
      placement: 'bottom' as const,
      target: '.target-header-help-center',
    },
  ];

  const handleStoreOnboarding = (onbStore: CallBackProps) => {
    const { action, index, status, type } = onbStore;
    const moveToNextStep = () => {
      setCurrentStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    };

    const closeOnboaridngChecklistMenu = () => {
      const sidebarCloseTriggerElement = document.getElementById('onboaridngChecklistsPopoverCloseTrigger');
      if (sidebarCloseTriggerElement) {
        sidebarCloseTriggerElement.click();
      }
    };

    const openMenu = () => {
      const menuElement = document.getElementById('onboaridngChecklistsPopover');
      if (menuElement) {
        menuElement.click();
      }
    };

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type as any)) {
      switch (index) {
        case 0:
          closeOnboaridngChecklistMenu();
          moveToNextStep();
          break;
        default:
          moveToNextStep();
          break;
      }
    } else if (STATUS.FINISHED === status) {
      gaEventTracker({
        event: AnalyticsEventEnum.onboardingTourGuidePassed,
      });
      dispatch(
        updateOnboardingTooltips({
          entity: EntityOnboardingTooltips.general_tooltip,
          status: TooltipsStatusesEnum.done,
        }),
      ).then((res) => {
        if (updateOnboardingTooltips.fulfilled.match(res)) {
          openMenu();
        }
      });

      dispatch(
        updateEntityOnboardingTooltip({
          entity: EntityOnboardingTooltips.general_tooltip,
          status: TooltipsStatusesEnum.done,
        }),
      );
      setRun(false);
      closeOnboaridngChecklistMenu();
    } else if (STATUS.SKIPPED === status) {
      gaEventTracker({
        event: AnalyticsEventEnum.onboardingTourGuideSkipped,
      });
      dispatch(
        updateOnboardingTooltips({
          entity: EntityOnboardingTooltips.general_tooltip,
          status: TooltipsStatusesEnum.skipped,
        }),
      ).then((res) => {
        if (updateOnboardingTooltips.fulfilled.match(res)) {
          openMenu();
        }
      });

      dispatch(
        updateEntityOnboardingTooltip({
          entity: EntityOnboardingTooltips.general_tooltip,
          status: TooltipsStatusesEnum.skipped,
        }),
      );
      setRun(false);
      closeOnboaridngChecklistMenu();
    }
  };

  const customTooltip = CustomTooltipTour({
    stepCount: ONBOARDING_TOOLTIP_STEPS.length,
  });
  const stepsWithDefaultOptions = mergedWithOptions(ONBOARDING_TOOLTIP_STEPS);

  useEffect(() => {
    if (isCompleteOnboarding) return;

    const openMenuAndStartTour = () => {
      gaEventTracker({
        event: AnalyticsEventEnum.onboardingTourGuideStarted,
      });
      const sidebarTriggerElement = document.getElementById('onboaridngChecklistsPopover');
      if (sidebarTriggerElement) {
        sidebarTriggerElement.click();

        setTimeout(() => {
          setRun(true);
        }, 300);
      }
    };

    const timerID = setTimeout(openMenuAndStartTour, 150);

    return () => {
      clearTimeout(timerID);
      setRun(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isCompleteOnboarding]);

  return (
    <Joyride
      steps={stepsWithDefaultOptions}
      beaconComponent={undefined}
      scrollToFirstStep
      continuous
      callback={handleStoreOnboarding}
      disableOverlay={false}
      scrollOffset={200}
      scrollDuration={500}
      disableScrolling={false}
      disableOverlayClose={true}
      floaterProps={{
        styles: {
          floater: {
            filter: 'none',
          },
          arrow: {
            color: theme.palette.case.neutral.n0,
            length: 7,
            spread: 9,
          },
        },
      }}
      run={run}
      stepIndex={currentStepIndex}
      tooltipComponent={customTooltip as any}
      styles={{
        options: {
          zIndex: 2000000,
          overlayColor: 'rgba(43, 50, 79, 0.25)',
        },
        spotlight: {
          backgroundColor: '#FFFFFF33',
          opacity: '0.2 !important',
        },
        overlay: {
          height: '100vh',
        },
      }}
    />
  );
};
